// API Repository for Codewave Frontend
// Base configuration and API routes

// const API_BASE_URL = 'http://localhost:5007/api/v1';
const API_BASE_URL = 'https://backend.codewavetechnology.com/api/v1';

export { API_BASE_URL };

// Debug logging for API calls (only in development)
if (process.env.NODE_ENV === 'development') {
  console.log('API Base URL:', API_BASE_URL);
}

// API Routes
export const API_ROUTES = {
  HERO_SECTION: '/hero-section',
  ABOUT_US: '/about-us',
  SERVICES_SECTION: '/services',
  HOW_IT_WORKS_SECTION: '/how-it-works',
  TEAM_SECTION: '/our-team',
  TESTIMONIALS_SECTION: '/testimonials',
  BLOG_SECTION: '/blog-section',
  CONTACT_SECTION: '/contact-section',
  IMAGES: '/images',
  // Blog API endpoints
  BLOGS: '/blogs',
  BLOG_BY_ID: '/blogs',
  BLOG_CATEGORIES: '/blogs/categories',
  BLOG_AUTHORS: '/blogs/authors',
  BLOG_TAGS: '/blogs/tags',
  // Contact API endpoints
  CONTACT: '/contact',
  // Pricing API endpoints
  PRICING: '/pricing',
  // Service Page API endpoint
  SERVICE_PAGE: '/service-page'
};

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
const CACHE_KEY_PREFIX = 'codewave_cache_';

// Cache storage
class CacheManager {
  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
  }

  set(key, value) {
    const cacheKey = `${CACHE_KEY_PREFIX}${key}`;
    this.cache.set(cacheKey, value);
    this.timestamps.set(cacheKey, Date.now());
  }

  get(key) {
    const cacheKey = `${CACHE_KEY_PREFIX}${key}`;
    const timestamp = this.timestamps.get(cacheKey);

    if (!timestamp || Date.now() - timestamp > CACHE_DURATION) {
      this.cache.delete(cacheKey);
      this.timestamps.delete(cacheKey);
      return null;
    }

    return this.cache.get(cacheKey);
  }

  clear() {
    this.cache.clear();
    this.timestamps.clear();
  }

  clearExpired() {
    const now = Date.now();
    for (const [key, timestamp] of this.timestamps.entries()) {
      if (now - timestamp > CACHE_DURATION) {
        this.cache.delete(key);
        this.timestamps.delete(key);
      }
    }
  }
}

// Global cache instance
export const cacheManager = new CacheManager();

// Clear cache on page refresh/reload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    cacheManager.clear();
  });
}

// Generic API fetch function with caching
export const fetchFromAPI = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const cacheKey = endpoint;
  const isDebug = process.env.NODE_ENV === 'development';

  if (isDebug) console.log(`Fetching from API: ${url}`);

  // Check cache first
  const cachedData = cacheManager.get(cacheKey);
  if (cachedData) {
    if (isDebug) console.log(`Using cached data for ${endpoint}`);
    return cachedData;
  }

  try {
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (isDebug) console.log(`API Response status: ${response.status} for ${endpoint}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (isDebug) console.log(`API Data received for ${endpoint}:`, data);

    // Cache the response
    cacheManager.set(cacheKey, data);

    return data;
  } catch (error) {
    console.error(`Error fetching from ${endpoint}:`, error);
    if (isDebug) {
      console.error(`Full error details:`, {
        message: error.message,
        stack: error.stack,
        url: url
      });
    }
    throw error;
  }
};

// Specific API functions
export const fetchHeroSection = () => fetchFromAPI(API_ROUTES.HERO_SECTION);
export const fetchAboutUs = () => fetchFromAPI(API_ROUTES.ABOUT_US);
export const fetchServicesSection = () => fetchFromAPI(API_ROUTES.SERVICES_SECTION);
export const fetchHowItWorksSection = () => fetchFromAPI(API_ROUTES.HOW_IT_WORKS_SECTION);
export const fetchTeamSection = () => fetchFromAPI(API_ROUTES.TEAM_SECTION);
export const fetchTestimonialsSection = () => fetchFromAPI(API_ROUTES.TESTIMONIALS_SECTION);
export const fetchBlogSection = () => fetchFromAPI(API_ROUTES.BLOG_SECTION);
export const fetchContactSection = () => fetchFromAPI(API_ROUTES.CONTACT_SECTION);

// Blog API functions
export const fetchBlogs = (params = {}) => {
  const queryParams = new URLSearchParams();

  // Add query parameters
  if (params.page) queryParams.append('page', params.page);
  if (params.per_page) queryParams.append('per_page', params.per_page);
  if (params.category) queryParams.append('category', params.category);
  if (params.author) queryParams.append('author', params.author);
  if (params.tag) queryParams.append('tag', params.tag);
  if (params.sort_by) queryParams.append('sort_by', params.sort_by);
  if (params.sort_order) queryParams.append('sort_order', params.sort_order);

  const queryString = queryParams.toString();
  const endpoint = queryString ? `${API_ROUTES.BLOGS}?${queryString}` : API_ROUTES.BLOGS;

  return fetchFromAPI(endpoint);
};

export const fetchBlogById = (blogId) => fetchFromAPI(`${API_ROUTES.BLOG_BY_ID}/${blogId}`);
export const fetchBlogCategories = () => fetchFromAPI(API_ROUTES.BLOG_CATEGORIES);
export const fetchBlogAuthors = () => fetchFromAPI(API_ROUTES.BLOG_AUTHORS);
export const fetchBlogTags = () => fetchFromAPI(API_ROUTES.BLOG_TAGS);

// Pricing API functions
export const fetchPricing = () => fetchFromAPI(API_ROUTES.PRICING);

// Service Page API functions
export const fetchServicePage = () => fetchFromAPI(API_ROUTES.SERVICE_PAGE);

// Contact API functions
export const submitContactForm = async (contactData) => {
  const isDebug = process.env.NODE_ENV === 'development';
  const url = `${API_BASE_URL}${API_ROUTES.CONTACT}`;

  if (isDebug) console.log('Submitting contact form to:', url);
  if (isDebug) console.log('Contact data:', contactData);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(contactData),
    });

    if (isDebug) console.log(`Contact form response status: ${response.status}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (isDebug) console.log('Contact form submission successful:', data);

    return data;
  } catch (error) {
    console.error('Error submitting contact form:', error);
    if (isDebug) {
      console.error('Full error details:', {
        message: error.message,
        stack: error.stack,
        url: url,
        contactData: contactData
      });
    }
    throw error;
  }
};

// Image fetching function
export const fetchImage = (imagePath) => {
  if (!imagePath) return null;
  return `${API_BASE_URL}${API_ROUTES.IMAGES}/${imagePath}`;
};

// Utility function to clear all cached data
export const clearAllCache = () => {
  cacheManager.clear();
};

// Utility function to clear expired cache entries
export const clearExpiredCache = () => {
  cacheManager.clearExpired();
};
